import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Building2, MapPin, TrendingUp, ExternalLink, ShoppingCart, ChevronDown, ChevronUp, Calendar as CalendarIcon, Clock, Briefcase, SkipForward, SkipBack, ListTodo } from "lucide-react";
import { formatPhoneNumber, formatCurrency } from "@/utils/formatting";
import LeadActionLog from "@/components/LeadActionLog";
import SelfRegisteredBadge from "@/components/SelfRegisteredBadge";
import { LeadSmartPanel } from "@/components/smartleads/LeadSmartPanel";
import { LeadProfileStrip } from "@/components/leads/LeadProfileStrip";
import { GingrStageHeader } from "@/components/leads/LeadStatusRules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEmails, setExpandedEmails] = useState({});
  const [nextLeadId, setNextLeadId] = useState(null);
  const [prevLeadId, setPrevLeadId] = useState(null);
  const [loadingNextLead, setLoadingNextLead] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Check where the user came from
  const fromSource = searchParams.get('from');
  const fromQueue = fromSource === 'queue';
  const fromList = fromSource === 'list';
  const [listTab, setListTab] = useState(null);

  useEffect(() => {
    fetchLeadDetail();
    if (fromQueue) {
      fetchNextLead();
    } else if (fromList) {
      computeListNeighbors();
    }
  }, [id, fromQueue, fromList]);

  const computeListNeighbors = () => {
    try {
      const raw = sessionStorage.getItem('leads_nav_list');
      if (!raw) return;
      const { ids, tab } = JSON.parse(raw);
      if (tab) setListTab(tab);
      if (!Array.isArray(ids)) return;
      const currentIndex = ids.findIndex((leadId) => leadId === id);
      if (currentIndex === -1) {
        setPrevLeadId(null);
        setNextLeadId(null);
        return;
      }
      setPrevLeadId(currentIndex > 0 ? ids[currentIndex - 1] : null);
      setNextLeadId(currentIndex < ids.length - 1 ? ids[currentIndex + 1] : null);
    } catch (e) {
      console.error('Failed to compute list neighbors:', e);
    }
  };

  const fetchLeadDetail = async () => {
    try {
      const response = await axios.get(`${API}/leads/${id}`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load lead details");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchNextLead = async () => {
    try {
      const response = await axios.get(`${API}/leads/follow-up-queue`);
      const queue = response.data;
      
      // Combine all priority leads and find the next one after current
      const allLeads = [
        ...(queue.high_priority || []),
        ...(queue.medium_priority || []),
        ...(queue.low_priority || [])
      ];
      
      // Find current lead's position and get the next and previous ones
      const currentIndex = allLeads.findIndex(lead => lead.id === id);
      if (currentIndex !== -1) {
        // Get next lead
        if (currentIndex < allLeads.length - 1) {
          setNextLeadId(allLeads[currentIndex + 1].id);
        } else {
          setNextLeadId(null);
        }
        // Get previous lead
        if (currentIndex > 0) {
          setPrevLeadId(allLeads[currentIndex - 1].id);
        } else {
          setPrevLeadId(null);
        }
      } else if (allLeads.length > 0) {
        // If current lead is not in queue, suggest first lead as next
        setNextLeadId(allLeads[0].id);
        setPrevLeadId(null);
      }
    } catch (error) {
      console.error("Failed to fetch next lead:", error);
    }
  };
  
  const handlePrevLead = () => {
    if (prevLeadId) {
      const param = fromList ? 'list' : 'queue';
      navigate(`/leads/${prevLeadId}?from=${param}`);
    }
  };
  
  const handleNextLead = () => {
    if (nextLeadId) {
      setLoadingNextLead(true);
      const param = fromList ? 'list' : 'queue';
      navigate(`/leads/${nextLeadId}?from=${param}`);
    } else if (fromList) {
      // No more leads - go back to leads list
      navigate(listTab ? `/leads?tab=${listTab}` : '/leads');
    } else {
      // No more leads - go back to queue
      navigate('/follow-up-queue');
    }
  };
  
  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'not_a_lead') {
        // Default reason on first transition; user can refine via inline dropdown
        payload.not_a_lead_reason = data?.lead?.not_a_lead_reason || 'general_inquiry';
      }
      await axios.patch(`${API}/leads/${id}/status`, payload);
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      fetchLeadDetail();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleNotALeadReasonChange = async (reason) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/leads/${id}/status`, {
        status: 'not_a_lead',
        not_a_lead_reason: reason,
      });
      toast.success("Reason updated");
      fetchLeadDetail();
    } catch (error) {
      toast.error("Failed to update reason");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePOSMatch = async () => {
    try {
      toast.info("Checking POS for matches...");
      const response = await axios.post(`${API}/pos/match/${id}`);
      if (response.data.matched) {
        toast.success("POS match found!");
        fetchLeadDetail();
      } else {
        toast.info("No POS match found");
      }
    } catch (error) {
      toast.error("Failed to check POS match");
    }
  };

  const toggleEmailExpand = (emailId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-600">Loading lead details...</div>
      </div>
    );
  }

  if (!data) return null;

  const { lead, email_logs, pos_matches } = data;

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <Button
          variant="ghost"
          onClick={() => {
            if (fromQueue) {
              navigate('/follow-up-queue');
            } else if (fromList) {
              navigate(listTab ? `/leads?tab=${listTab}` : '/leads');
            } else {
              navigate(-1);
            }
          }}
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {fromQueue ? 'Back to Queue' : fromList ? 'Back to Leads' : 'Back'}
        </Button>
        
        {/* Navigation buttons - shown when coming from queue or leads list */}
        {(fromQueue || fromList) && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handlePrevLead}
              disabled={!prevLeadId}
              data-testid="prev-lead-button"
            >
              <SkipBack className="h-4 w-4 mr-2" />
              Previous
            </Button>
            {fromQueue && (
              <Button
                variant="outline"
                onClick={() => navigate('/follow-up-queue')}
                data-testid="view-queue-button"
              >
                <ListTodo className="h-4 w-4 mr-2" />
                View Queue
              </Button>
            )}
            <Button
              onClick={handleNextLead}
              disabled={loadingNextLead || (fromList && !nextLeadId)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
              data-testid="next-lead-button"
            >
              <SkipForward className="h-4 w-4 mr-2" />
              {nextLeadId
                ? 'Next Lead'
                : fromList
                ? 'No More Leads'
                : 'Back to Queue'}
            </Button>
          </div>
        )}
      </div>

      <LeadSmartPanel leadId={lead.id} onChange={fetchLeadDetail} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <CardTitle className="text-3xl" style={{ fontFamily: 'Manrope' }}>
                      {lead.name || "Unknown Lead"}
                    </CardTitle>
                    {lead.self_registered && <SelfRegisteredBadge size="lg" withPulse />}
                  </div>
                  
                  {/* Status Selector Dropdown */}
                  <div className="flex items-center gap-3 mt-3">
                    <Select 
                      value={lead.status} 
                      onValueChange={handleStatusChange}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger className={`w-[180px] status-badge status-${lead.status}`} data-testid="status-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="existing_customer">Existing Customer</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                        <SelectItem value="not_a_lead">Not a Lead</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Sub-reason dropdown when status is "Not a Lead" */}
                    {lead.status === 'not_a_lead' && (
                      <Select
                        value={lead.not_a_lead_reason || 'general_inquiry'}
                        onValueChange={handleNotALeadReasonChange}
                        disabled={updatingStatus}
                      >
                        <SelectTrigger
                          className="w-[220px] h-9 text-xs border-amber-300 bg-amber-50 text-amber-900"
                          data-testid="not-a-lead-reason-selector"
                        >
                          <SelectValue placeholder="Reason" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="job_application">Job Application</SelectItem>
                          <SelectItem value="general_inquiry">General Inquiry / Misc Question</SelectItem>
                          <SelectItem value="spam">Spam / Bot</SelectItem>
                          <SelectItem value="vendor">Vendor / Solicitation</SelectItem>
                          <SelectItem value="wrong_number">Wrong Number / Wrong Business</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Re-open button for converted/lost/not_a_lead leads */}
                    {(lead.status === 'converted' || lead.status === 'lost' || lead.status === 'not_a_lead') && (
                      <Button
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API}/leads/${lead.id}/reopen`);
                            if (response.data.success) {
                              toast.success("Lead re-opened successfully!");
                              fetchLeadDetail();
                            }
                          } catch (error) {
                            toast.error("Failed to re-open lead");
                          }
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Re-open Lead
                      </Button>
                    )}
                  </div>
                  <div className="mt-3"><LeadProfileStrip leadId={lead.id} /></div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500 mb-1">Lead Score</div>
                  <div className="text-4xl font-bold text-cyan-600" style={{ fontFamily: 'Manrope' }}>{lead.score}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {lead.canonical_email && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Mail className="h-5 w-5 text-cyan-500" />
                  <span>{lead.canonical_email}</span>
                </div>
              )}
              {lead.primary_phone && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Phone className="h-5 w-5 text-cyan-500" />
                  <span>{formatPhoneNumber(lead.primary_phone)}</span>
                </div>
              )}
              {lead.company && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Building2 className="h-5 w-5 text-cyan-500" />
                  <span>{lead.company}</span>
                </div>
              )}
              {lead.address && (
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="h-5 w-5 text-cyan-500" />
                  <span>{lead.address}</span>
                </div>
              )}
              {lead.service && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Briefcase className="h-5 w-5 text-cyan-500" />
                  <span className="font-medium">{lead.service}</span>
                </div>
              )}

              <Separator className="my-4" />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 mb-1">Source</div>
                  <div className="font-medium">{lead.source}</div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Created</div>
                  <div className="font-medium">{new Date(lead.created_at).toLocaleString()}</div>
                </div>
                {lead.desired_service && (
                  <div>
                    <div className="text-gray-500 mb-1">Service Requested</div>
                    <div className="font-medium text-blue-600">{lead.desired_service}</div>
                  </div>
                )}
                {lead.desired_date && (
                  <div>
                    <div className="text-gray-500 mb-1">Desired Date</div>
                    <div className="font-medium flex items-center gap-1">
                      <CalendarIcon className="h-3 w-3" />
                      {lead.desired_date}
                    </div>
                  </div>
                )}
                {lead.preferred_time && (
                  <div>
                    <div className="text-gray-500 mb-1">Preferred Time</div>
                    <div className="font-medium flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {lead.preferred_time}
                    </div>
                  </div>
                )}
                {lead.zip_code && (
                  <div>
                    <div className="text-gray-500 mb-1">Zip Code</div>
                    <div className="font-medium">{lead.zip_code}</div>
                  </div>
                )}
                {lead.utm_source && (
                  <div>
                    <div className="text-gray-500 mb-1">UTM Source</div>
                    <div className="font-medium">{lead.utm_source}</div>
                  </div>
                )}
                {lead.utm_campaign && (
                  <div>
                    <div className="text-gray-500 mb-1">UTM Campaign</div>
                    <div className="font-medium">{lead.utm_campaign}</div>
                  </div>
                )}
              </div>

              {lead.details && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <div className="text-gray-500 mb-2 text-sm">Additional Details</div>
                    <div className="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap text-sm">
                      {lead.details}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Staff Actions - Moved to top for easy access */}
          <LeadActionLog 
            leadId={lead.id} 
            leadStatus={lead.status}
            actionLog={lead.action_log || []}
            onActionAdded={fetchLeadDetail}
          />

          {/* Email Thread */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Email Thread</CardTitle>
            </CardHeader>
            <CardContent>
              {email_logs.length > 0 ? (
                <div className="space-y-4">
                  {email_logs.map((log) => {
                    const isExpanded = expandedEmails[log.id];
                    const displayText = isExpanded ? (log.body_text_full || log.body_text) : log.body_text;
                    const hasMore = log.body_text_full && log.body_text_full.length > log.body_text?.length;
                    
                    return (
                      <div key={log.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-semibold text-gray-900">{log.subject || "(No Subject)"}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(log.received_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          From: <span className="font-medium">{log.from_email}</span>
                        </div>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">
                          {displayText || "(No content)"}
                        </div>
                        {hasMore && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleEmailExpand(log.id)}
                            className="mt-2 text-cyan-600 hover:text-cyan-700"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="h-4 w-4 mr-1" />
                                Show Less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-4 w-4 mr-1" />
                                Show Full Email
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No email activity yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* POS Matches */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <ShoppingCart className="h-5 w-5" />
                POS Matches
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pos_matches.length > 0 ? (
                <div className="space-y-4">
                  {pos_matches.map((match) => (
                    <div key={match.id} className="p-4 bg-green-50 border-2 border-green-400 rounded-lg">
                      <GingrStageHeader leadId={lead.id} status={lead.status} />
                      
                      {/* Revenue Display - Prominent */}
                      {match.pos_payload?.total_revenue && (
                        <div className="mb-3 p-3 bg-white rounded-lg border-2 border-green-300">
                          <div className="text-xs text-gray-600 mb-1">Total Revenue</div>
                          <div className="text-3xl font-bold text-green-700">
                            {formatCurrency(match.pos_payload.total_revenue)}
                          </div>
                          {match.pos_payload?.deposits_collected && parseFloat(match.pos_payload.deposits_collected) > 0 && (
                            <div className="text-xs text-gray-600 mt-2">
                              Closed Invoices: {formatCurrency(match.pos_payload.lifetime_revenue || '0.00')} | 
                              Deposits: {formatCurrency(match.pos_payload.deposits_collected)}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {match.pos_payload?.customer_id && (
                        <div className="text-sm text-gray-700 mb-1">Customer ID: #{match.pos_payload.customer_id}</div>
                      )}
                      {match.pos_payload?.animal_names && (
                        <div className="text-sm text-gray-700 mb-1">Pets: {match.pos_payload.animal_names}</div>
                      )}
                      {Number(match.pos_payload?.number_reservations) > 0 && (
                        <div className="text-sm text-gray-700 mb-1">Reservations: {match.pos_payload.number_reservations}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-2">
                        Matched: {new Date(match.matched_at).toLocaleDateString()}
                      </div>
                      {data.gingr_app_url && match.pos_customer_id && (
                        <Button asChild className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`open-in-gingr-${match.pos_customer_id}`}>
                          <a href={`${data.gingr_app_url}/owners/view/id/${match.pos_customer_id}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-2" /> Open in Gingr
                          </a>
                        </Button>
                      )}
                      <div className="mt-3 p-2 bg-white rounded border border-green-300">
                        <p className="text-xs text-green-800">
                          <strong>In Gingr.</strong> Personalize your follow-up with their pet names and service history.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <p className="text-gray-500 text-sm mb-4">No POS matches found yet</p>
                  <Button
                    onClick={handlePOSMatch}
                    variant="outline"
                    className="w-full"
                    data-testid="check-pos-button"
                  >
                    Check POS Now
                  </Button>
                  
                  {/* Manual conversion button */}
                  {lead.status !== 'converted' && (
                    <Button
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/leads/${lead.id}/convert`);
                          if (response.data.success) {
                            toast.success("Lead marked as converted!");
                            fetchLeadDetail();
                          }
                        } catch (error) {
                          toast.error("Failed to mark as converted");
                        }
                      }}
                      className="w-full mt-2 bg-green-600 hover:bg-green-700"
                    >
                      Mark as Converted
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Score Breakdown */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <TrendingUp className="h-5 w-5" />
                Score Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Base Score</span>
                <span className="font-semibold">+10</span>
              </div>
              {lead.utm_source && (
                <div className="flex justify-between">
                  <span className="text-gray-600">UTM Source</span>
                  <span className="font-semibold text-green-600">+30</span>
                </div>
              )}
              {lead.company && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Has Company</span>
                  <span className="font-semibold text-green-600">+15</span>
                </div>
              )}
              {lead.primary_phone && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Has Phone</span>
                  <span className="font-semibold text-green-600">+10</span>
                </div>
              )}
              {pos_matches.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">POS Match</span>
                  <span className="font-semibold text-green-600">+50</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-base">
                <span className="font-bold">Total Score</span>
                <span className="font-bold text-cyan-600">{lead.score}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default LeadDetail;
